import { InternalApiAlertFilter } from '../../api-internal';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface AlertFiltersState {
  filters: Array<InternalApiAlertFilter>;
  loadingAlertFilters: boolean;
  latestUpdatedFilter: InternalApiAlertFilter | undefined;
  lastFetched: Date | undefined;
}

export const initialState: AlertFiltersState = {
  filters: [],
  loadingAlertFilters: false,
  latestUpdatedFilter: undefined,
  // Backend might return empty filters array in 2 cases:
  //   1. There are no filters configured yet
  //   2. User has no filters assigned to them
  // Because of the above, we cannot know for sure if we have fetched filters already and they're empty, or we should
  // fetch now for the first time. To avoid fetching filters every time we have empty array, `lastFetched` is set
  // to a timestamp so if needed, we can re-fetch them, or use the ones stored here.
  lastFetched: undefined,
};

const sorted = (filters: Array<InternalApiAlertFilter>): Array<InternalApiAlertFilter> =>
  filters.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));

const alertFiltersSlice = createSlice({
  name: 'alertFilters',
  initialState,
  reducers: {
    fetchAlertFiltersSuccess: (state, action: PayloadAction<Array<InternalApiAlertFilter>>) => {
      state.filters = sorted(action.payload);
      state.loadingAlertFilters = false;
      state.lastFetched = new Date();
    },
    addNewAlertFilterSuccess: (state, action: PayloadAction<InternalApiAlertFilter>) => {
      const updatedFilters = state.filters.concat(action.payload);
      state.filters = sorted(updatedFilters);
      state.latestUpdatedFilter = action.payload;
    },
    updateAlertFilterSuccess: (state, action: PayloadAction<InternalApiAlertFilter>) => {
      state.filters = state.filters.map((alertFilter) => {
        return alertFilter.id !== action.payload.id ? alertFilter : action.payload;
      });
      state.latestUpdatedFilter = action.payload;
    },
    deleteAlertFilterSuccess: (state, action: PayloadAction<number>) => {
      state.filters = state.filters.filter((alertFilter) => alertFilter.id !== action.payload);
    },
    setLoadingAlertFilters: (state, action: PayloadAction<boolean>) => {
      state.loadingAlertFilters = action.payload;
    },
  },
});

const { actions, reducer } = alertFiltersSlice;
export const {
  fetchAlertFiltersSuccess,
  setLoadingAlertFilters,
  addNewAlertFilterSuccess,
  updateAlertFilterSuccess,
  deleteAlertFilterSuccess,
} = actions;
export default reducer;
